import React, { createContext, PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';

function isElement(el) {
  return el != null && typeof el === 'object' && el.nodeType === 1;
}

function canOverflow(overflow, skipOverflowHiddenElements) {
  if (skipOverflowHiddenElements && overflow === 'hidden') {
    return false;
  }

  return overflow !== 'visible' && overflow !== 'clip';
}

function isScrollable(el, skipOverflowHiddenElements) {
  if (el.clientHeight < el.scrollHeight || el.clientWidth < el.scrollWidth) {
    var style = getComputedStyle(el, null);
    return canOverflow(style.overflowY, skipOverflowHiddenElements) || canOverflow(style.overflowX, skipOverflowHiddenElements);
  }

  return false;
}

function alignNearest(scrollingEdgeStart, scrollingEdgeEnd, scrollingSize, scrollingBorderStart, scrollingBorderEnd, elementEdgeStart, elementEdgeEnd, elementSize) {
  if (elementEdgeStart < scrollingEdgeStart && elementEdgeEnd > scrollingEdgeEnd || elementEdgeStart > scrollingEdgeStart && elementEdgeEnd < scrollingEdgeEnd) {
    return 0;
  }

  if (elementEdgeStart <= scrollingEdgeStart && elementSize <= scrollingSize || elementEdgeEnd >= scrollingEdgeEnd && elementSize >= scrollingSize) {
    return elementEdgeStart - scrollingEdgeStart - scrollingBorderStart;
  }

  if (elementEdgeEnd > scrollingEdgeEnd && elementSize < scrollingSize || elementEdgeStart < scrollingEdgeStart && elementSize > scrollingSize) {
    return elementEdgeEnd - scrollingEdgeEnd + scrollingBorderEnd;
  }

  return 0;
}

var compute = (function (target, options) {
  var scrollMode = options.scrollMode,
      block = options.block,
      inline = options.inline,
      boundary = options.boundary,
      skipOverflowHiddenElements = options.skipOverflowHiddenElements;
  var checkBoundary = typeof boundary === 'function' ? boundary : function (node) {
    return node !== boundary;
  };

  if (!isElement(target)) {
    throw new TypeError('Invalid target');
  }

  var scrollingElement = document.scrollingElement || document.documentElement;
  var frames = [];
  var cursor = target;

  while (isElement(cursor) && checkBoundary(cursor)) {
    cursor = cursor.parentNode;

    if (cursor === scrollingElement) {
      frames.push(cursor);
      break;
    }

    if (cursor === document.body && isScrollable(cursor) && !isScrollable(document.documentElement)) {
      continue;
    }

    if (isScrollable(cursor, skipOverflowHiddenElements)) {
      frames.push(cursor);
    }
  }

  var viewportWidth = window.visualViewport ? visualViewport.width : innerWidth;
  var viewportHeight = window.visualViewport ? visualViewport.height : innerHeight;
  var viewportX = window.scrollX || pageXOffset;
  var viewportY = window.scrollY || pageYOffset;

  var _target$getBoundingCl = target.getBoundingClientRect(),
      targetHeight = _target$getBoundingCl.height,
      targetWidth = _target$getBoundingCl.width,
      targetTop = _target$getBoundingCl.top,
      targetRight = _target$getBoundingCl.right,
      targetBottom = _target$getBoundingCl.bottom,
      targetLeft = _target$getBoundingCl.left;

  var targetBlock = block === 'start' || block === 'nearest' ? targetTop : block === 'end' ? targetBottom : targetTop + targetHeight / 2;
  var targetInline = inline === 'center' ? targetLeft + targetWidth / 2 : inline === 'end' ? targetRight : targetLeft;
  var computations = [];

  for (var index = 0; index < frames.length; index++) {
    var frame = frames[index];

    var _frame$getBoundingCli = frame.getBoundingClientRect(),
        _height = _frame$getBoundingCli.height,
        _width = _frame$getBoundingCli.width,
        _top = _frame$getBoundingCli.top,
        right = _frame$getBoundingCli.right,
        bottom = _frame$getBoundingCli.bottom,
        _left = _frame$getBoundingCli.left;

    if (scrollMode === 'if-needed' && targetTop >= 0 && targetLeft >= 0 && targetBottom <= viewportHeight && targetRight <= viewportWidth && targetTop >= _top && targetBottom <= bottom && targetLeft >= _left && targetRight <= right) {
      return computations;
    }

    var frameStyle = getComputedStyle(frame);
    var borderLeft = parseInt(frameStyle.borderLeftWidth, 10);
    var borderTop = parseInt(frameStyle.borderTopWidth, 10);
    var borderRight = parseInt(frameStyle.borderRightWidth, 10);
    var borderBottom = parseInt(frameStyle.borderBottomWidth, 10);
    var blockScroll = 0;
    var inlineScroll = 0;
    var scrollbarWidth = 'offsetWidth' in frame ? frame.offsetWidth - frame.clientWidth - borderLeft - borderRight : 0;
    var scrollbarHeight = 'offsetHeight' in frame ? frame.offsetHeight - frame.clientHeight - borderTop - borderBottom : 0;

    if (scrollingElement === frame) {
      if (block === 'start') {
        blockScroll = targetBlock;
      } else if (block === 'end') {
        blockScroll = targetBlock - viewportHeight;
      } else if (block === 'nearest') {
        blockScroll = alignNearest(viewportY, viewportY + viewportHeight, viewportHeight, borderTop, borderBottom, viewportY + targetBlock, viewportY + targetBlock + targetHeight, targetHeight);
      } else {
        blockScroll = targetBlock - viewportHeight / 2;
      }

      if (inline === 'start') {
        inlineScroll = targetInline;
      } else if (inline === 'center') {
        inlineScroll = targetInline - viewportWidth / 2;
      } else if (inline === 'end') {
        inlineScroll = targetInline - viewportWidth;
      } else {
        inlineScroll = alignNearest(viewportX, viewportX + viewportWidth, viewportWidth, borderLeft, borderRight, viewportX + targetInline, viewportX + targetInline + targetWidth, targetWidth);
      }

      blockScroll = Math.max(0, blockScroll + viewportY);
      inlineScroll = Math.max(0, inlineScroll + viewportX);
    } else {
      if (block === 'start') {
        blockScroll = targetBlock - _top - borderTop;
      } else if (block === 'end') {
        blockScroll = targetBlock - bottom + borderBottom + scrollbarHeight;
      } else if (block === 'nearest') {
        blockScroll = alignNearest(_top, bottom, _height, borderTop, borderBottom + scrollbarHeight, targetBlock, targetBlock + targetHeight, targetHeight);
      } else {
        blockScroll = targetBlock - (_top + _height / 2) + scrollbarHeight / 2;
      }

      if (inline === 'start') {
        inlineScroll = targetInline - _left - borderLeft;
      } else if (inline === 'center') {
        inlineScroll = targetInline - (_left + _width / 2) + scrollbarWidth / 2;
      } else if (inline === 'end') {
        inlineScroll = targetInline - right + borderRight + scrollbarWidth;
      } else {
        inlineScroll = alignNearest(_left, right, _width, borderLeft, borderRight + scrollbarWidth, targetInline, targetInline + targetWidth, targetWidth);
      }

      var scrollLeft = frame.scrollLeft,
          scrollTop = frame.scrollTop;
      blockScroll = Math.max(0, Math.min(scrollTop + blockScroll, frame.scrollHeight - _height + scrollbarHeight));
      inlineScroll = Math.max(0, Math.min(scrollLeft + inlineScroll, frame.scrollWidth - _width + scrollbarWidth));
      targetBlock += scrollTop - blockScroll;
      targetInline += scrollLeft - inlineScroll;
    }

    computations.push({
      el: frame,
      top: blockScroll,
      left: inlineScroll
    });
  }

  return computations;
});

function isOptionsObject(options) {
  return options === Object(options) && Object.keys(options).length !== 0;
}

function defaultBehavior(actions, behavior) {
  if (behavior === void 0) {
    behavior = 'auto';
  }

  var canSmoothScroll = 'scrollBehavior' in document.body.style;
  actions.forEach(function (_ref) {
    var el = _ref.el,
        top = _ref.top,
        left = _ref.left;

    if (el.scroll && canSmoothScroll) {
      el.scroll({
        top: top,
        left: left,
        behavior: behavior
      });
    } else {
      el.scrollTop = top;
      el.scrollLeft = left;
    }
  });
}

function getOptions(options) {
  if (options === false) {
    return {
      block: 'end',
      inline: 'nearest'
    };
  }

  if (isOptionsObject(options)) {
    return options;
  }

  return {
    block: 'start',
    inline: 'nearest'
  };
}

function scrollIntoView(target, options) {
  var targetIsDetached = !target.ownerDocument.documentElement.contains(target);

  if (isOptionsObject(options) && typeof options.behavior === 'function') {
    return options.behavior(targetIsDetached ? [] : compute(target, options));
  }

  if (targetIsDetached) {
    return;
  }

  var computeOptions = getOptions(options);
  return defaultBehavior(compute(target, computeOptions), computeOptions.behavior);
}

var PaginationContext = createContext();

var classCallCheck = function (instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
};

var createClass = function () {
  function defineProperties(target, props) {
    for (var i = 0; i < props.length; i++) {
      var descriptor = props[i];
      descriptor.enumerable = descriptor.enumerable || false;
      descriptor.configurable = true;
      if ("value" in descriptor) descriptor.writable = true;
      Object.defineProperty(target, descriptor.key, descriptor);
    }
  }

  return function (Constructor, protoProps, staticProps) {
    if (protoProps) defineProperties(Constructor.prototype, protoProps);
    if (staticProps) defineProperties(Constructor, staticProps);
    return Constructor;
  };
}();

var inherits = function (subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function, not " + typeof superClass);
  }

  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      enumerable: false,
      writable: true,
      configurable: true
    }
  });
  if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass;
};

var possibleConstructorReturn = function (self, call) {
  if (!self) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return call && (typeof call === "object" || typeof call === "function") ? call : self;
};

var slicedToArray = function () {
  function sliceIterator(arr, i) {
    var _arr = [];
    var _n = true;
    var _d = false;
    var _e = undefined;

    try {
      for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
        _arr.push(_s.value);

        if (i && _arr.length === i) break;
      }
    } catch (err) {
      _d = true;
      _e = err;
    } finally {
      try {
        if (!_n && _i["return"]) _i["return"]();
      } finally {
        if (_d) throw _e;
      }
    }

    return _arr;
  }

  return function (arr, i) {
    if (Array.isArray(arr)) {
      return arr;
    } else if (Symbol.iterator in Object(arr)) {
      return sliceIterator(arr, i);
    } else {
      throw new TypeError("Invalid attempt to destructure non-iterable instance");
    }
  };
}();

var toConsumableArray = function (arr) {
  if (Array.isArray(arr)) {
    for (var i = 0, arr2 = Array(arr.length); i < arr.length; i++) arr2[i] = arr[i];

    return arr2;
  } else {
    return Array.from(arr);
  }
};

var Pagination = function (_PureComponent) {
  inherits(Pagination, _PureComponent);

  function Pagination() {
    var _ref;

    var _temp, _this, _ret;

    classCallCheck(this, Pagination);

    for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    return _ret = (_temp = (_this = possibleConstructorReturn(this, (_ref = Pagination.__proto__ || Object.getPrototypeOf(Pagination)).call.apply(_ref, [this].concat(args))), _this), _this.state = {
      nextChild: null,
      previousChild: null
    }, _this.setChildrenToScroll = function () {
      var containerScrollLeft = _this.scrollContainerRef.current.scrollLeft;
      var containerWidth = _this.scrollContainerRef.current.offsetWidth;

      _this.setState({
        nextChild: _this.chooseNextChild(containerScrollLeft, containerWidth),
        previousChild: _this.choosePreviousChild(containerScrollLeft)
      });
    }, _this.paginationOnScroll = function () {
      window.clearTimeout(_this.isScrolling);

      _this.isScrolling = setTimeout(function () {
        _this.setChildrenToScroll();
      }, 50);
    }, _this.chooseNextChild = function (containerScrollLeft, containerWidth) {
      var _filter = [].concat(toConsumableArray(_this.scrollContainerRef.current.children)).filter(function (child) {
        return _this.isNextVisible({ child: child, containerScrollLeft: containerScrollLeft, containerWidth: containerWidth });
      }),
          _filter2 = slicedToArray(_filter, 1),
          nextChild = _filter2[0];

      return nextChild;
    }, _this.choosePreviousChild = function (containerScrollLeft) {
      return [].concat(toConsumableArray(_this.scrollContainerRef.current.children)).filter(function (child) {
        return _this.isPreviousVisible({ child: child, containerScrollLeft: containerScrollLeft });
      }).pop();
    }, _this.isNextVisible = function (_ref2) {
      var child = _ref2.child,
          containerScrollLeft = _ref2.containerScrollLeft,
          containerWidth = _ref2.containerWidth;
      var offsetLeft = child.offsetLeft,
          offsetWidth = child.offsetWidth;

      return offsetLeft + offsetWidth > containerScrollLeft + containerWidth;
    }, _this.isPreviousVisible = function (_ref3) {
      var child = _ref3.child,
          containerScrollLeft = _ref3.containerScrollLeft;
      var offsetLeft = child.offsetLeft;

      return offsetLeft < containerScrollLeft;
    }, _this.scrollNext = function (event) {
      var containerScrollLeft = _this.scrollContainerRef.current.scrollLeft;
      var containerWidth = _this.scrollContainerRef.current.offsetWidth;
      var nextChild = _this.chooseNextChild(containerScrollLeft, containerWidth);

      _this.setState({ nextChild: nextChild });

      if (!nextChild) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      scrollIntoView(nextChild, { block: 'nearest', inline: 'center', behavior: 'smooth' });
    }, _this.scrollPrevious = function (event) {
      var containerScrollLeft = _this.scrollContainerRef.current.scrollLeft;
      var previousChild = _this.choosePreviousChild(containerScrollLeft);

      _this.setState({ previousChild: previousChild });

      if (!previousChild) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      scrollIntoView(previousChild, { block: 'nearest', inline: 'center', behavior: 'smooth' });
    }, _this.scrollContainerRef = React.createRef(), _temp), possibleConstructorReturn(_this, _ret);
  }

  createClass(Pagination, [{
    key: 'componentDidMount',
    value: function componentDidMount() {
      this.setChildrenToScroll();
      window.addEventListener('resize', this.setChildrenToScroll);
    }
  }, {
    key: 'componentWillUnmount',
    value: function componentWillUnmount() {
      window.removeEventListener('resize', this.setChildrenToScroll);
    }
  }, {
    key: 'render',
    value: function render() {
      var scrollContainerRef = this.scrollContainerRef,
          scrollNext = this.scrollNext,
          scrollPrevious = this.scrollPrevious,
          paginationOnScroll = this.paginationOnScroll,
          setChildrenToScroll = this.setChildrenToScroll;
      var _state = this.state,
          previousChild = _state.previousChild,
          nextChild = _state.nextChild;
      var children = this.props.children;


      return React.createElement(
        PaginationContext.Provider,
        { value: {
            nextChild: nextChild,
            previousChild: previousChild,
            scrollContainerRef: scrollContainerRef,
            setChildrenToScroll: setChildrenToScroll,
            scrollNext: scrollNext,
            scrollPrevious: scrollPrevious,
            paginationOnScroll: paginationOnScroll
          }
        },
        children
      );
    }
  }]);
  return Pagination;
}(PureComponent);

Pagination.propTypes = {
  children: PropTypes.node.isRequired
};

var NextButton = function (_PureComponent) {
  inherits(NextButton, _PureComponent);

  function NextButton() {
    classCallCheck(this, NextButton);
    return possibleConstructorReturn(this, (NextButton.__proto__ || Object.getPrototypeOf(NextButton)).apply(this, arguments));
  }

  createClass(NextButton, [{
    key: 'render',
    value: function render() {
      var View = this.props.render;
      var _context = this.context,
          scrollNext = _context.scrollNext,
          nextChild = _context.nextChild;


      return React.createElement(
        Fragment,
        null,
        nextChild && React.createElement(View, { scrollNext: scrollNext })
      );
    }
  }]);
  return NextButton;
}(PureComponent);

NextButton.propTypes = {
  render: PropTypes.func.isRequired
};
NextButton.contextType = PaginationContext;

var PreviousButton = function (_PureComponent) {
  inherits(PreviousButton, _PureComponent);

  function PreviousButton() {
    classCallCheck(this, PreviousButton);
    return possibleConstructorReturn(this, (PreviousButton.__proto__ || Object.getPrototypeOf(PreviousButton)).apply(this, arguments));
  }

  createClass(PreviousButton, [{
    key: 'render',
    value: function render() {
      var _context = this.context,
          scrollPrevious = _context.scrollPrevious,
          previousChild = _context.previousChild;
      var View = this.props.render;


      return React.createElement(
        Fragment,
        null,
        previousChild && React.createElement(View, { scrollPrevious: scrollPrevious })
      );
    }
  }]);
  return PreviousButton;
}(PureComponent);

PreviousButton.propTypes = {
  render: PropTypes.func.isRequired
};
PreviousButton.contextType = PaginationContext;

var ScrollContainer = function (_PureComponent) {
  inherits(ScrollContainer, _PureComponent);

  function ScrollContainer() {
    classCallCheck(this, ScrollContainer);
    return possibleConstructorReturn(this, (ScrollContainer.__proto__ || Object.getPrototypeOf(ScrollContainer)).apply(this, arguments));
  }

  createClass(ScrollContainer, [{
    key: 'componentDidUpdate',
    value: function componentDidUpdate() {
      var setChildrenToScroll = this.context.setChildrenToScroll;

      setChildrenToScroll();
    }
  }, {
    key: 'render',
    value: function render() {
      var _context = this.context,
          scrollContainerRef = _context.scrollContainerRef,
          paginationOnScroll = _context.paginationOnScroll;
      var View = this.props.render;


      return React.createElement(View, { reference: scrollContainerRef, onScroll: paginationOnScroll });
    }
  }]);
  return ScrollContainer;
}(PureComponent);

ScrollContainer.propTypes = {
  render: PropTypes.func.isRequired
};
ScrollContainer.contextType = PaginationContext;

var index = {
  Pagination: Pagination,
  NextButton: NextButton,
  PreviousButton: PreviousButton,
  ScrollContainer: ScrollContainer
};

export default index;
//# sourceMappingURL=index.es.js.map
